/* eslint-disable react/require-default-props */
import React, { useCallback, useContext, useState } from 'react';
import {
  Body1,
  Body1Strong,
  Button,
  Caption1,
  DrawerBody,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerFooter,
  Link,
  mergeClasses,
} from '@fluentui/react-components';
import { Dismiss24Regular, ChevronRight12Regular } from '@fluentui/react-icons';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { StackedDrawer } from '@bingads-webui-campaign-react/drawer-stack';

import { useTroubleshootDetailDrawerStyles } from './use-troubleshoot-detail-drawer-styles';
import { CONTENT_MAP, CONTACT_SUPPORT_URL } from './troubleshoot-detail-content';
import type { TroubleshootOption } from './troubleshoot-detail-content';

export type { TroubleshootOption };

export interface TroubleshootDetailDrawerProps {
  option: TroubleshootOption | null;
  onClose: () => void;
  logger?: {
    // eslint-disable-next-line no-unused-vars
    trace: (message: string) => void;
  };
}

const boldModel = {
  b: (text: string) => <strong>{text}</strong>,
};

const boldItalicModel = {
  b: (text: string) => <strong>{text}</strong>,
  i: (text: string) => <em>{text}</em>,
};

export const TroubleshootDetailDrawer: React.FC<TroubleshootDetailDrawerProps> = ({
  option,
  onClose,
  logger,
}) => {
  const i18n = useContext(I18nContext);
  const styles = useTroubleshootDetailDrawerStyles();
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(() => new Set());
  const [expandedAccordions, setExpandedAccordions] = useState<Set<number>>(() => new Set());
  const [expandedWhatToDo, setExpandedWhatToDo] = useState<Set<number>>(() => new Set());

  const toggleStep = useCallback((index: number, title: string) => {
    logger?.trace(`TroubleshootDetailDrawer: Toggled step expand="${title}"`);
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, [logger]);

  const toggleAccordion = useCallback((index: number, title: string) => {
    logger?.trace(`TroubleshootDetailDrawer: Toggled accordion="${title}"`);
    setExpandedAccordions((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, [logger]);

  const toggleWhatToDo = useCallback((index: number) => {
    logger?.trace('TroubleshootDetailDrawer: Toggled "What to do" section');
    setExpandedWhatToDo((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, [logger]);

  const handleClose = useCallback(() => {
    logger?.trace('TroubleshootDetailDrawer: Closed detail drawer');
    setExpandedSteps(new Set());
    setExpandedAccordions(new Set());
    setExpandedWhatToDo(new Set());
    onClose();
  }, [onClose, logger]);

  if (!option) return null;

  const content = CONTENT_MAP[option];

  return (
    <StackedDrawer
      className="troubleshoot-detail-drawer"
      name="troubleshoot-detail"
      onOpenChange={(_, { open: isOpen }) => {
        if (!isOpen) {
          handleClose();
        }
      }}
    >
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label={i18n.getString(_TL_('Close'))}
              icon={<Dismiss24Regular />}
              onClick={handleClose}
            />
          }
        >
          {i18n.getString(content.title)}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody className={mergeClasses('troubleshoot-detail-body', styles.body)}>
        <div className={styles.contentSection}>
          <div className={styles.problemSection}>
            {content.problemDescription && (
              <Body1 className={styles.problemDescription}>
                {i18n.getString(content.problemDescription)}
              </Body1>
            )}
            {content.problemDetail && (
              <Body1 className={styles.problemDescription}>
                {i18n.getString(content.problemDetail)}
              </Body1>
            )}
          </div>

          <div className={mergeClasses('troubleshoot-detail-steps-section', styles.stepsSection)}>
            <div className={styles.stepsHeader}>
              {content.stepsTitle && (
                <Body1Strong>{i18n.getString(content.stepsTitle)}</Body1Strong>
              )}
              {content.steps.length > 0 ? (
                <Body1Strong className={styles.stepsSubtitle}>
                  {i18n.getString(content.stepsSubtitle)}
                </Body1Strong>
              ) : (
                <Body1 className={styles.stepsSubtitle}>
                  {i18n.getString(content.stepsSubtitle)}
                </Body1>
              )}
              {content.stepsTip && (
                <Caption1 className={styles.stepsTip}>
                  <ReactTemplate
                    template={i18n.getString(content.stepsTip)}
                    model={boldModel}
                  />
                </Caption1>
              )}
            </div>

            <div className={styles.stepsList}>
              {content.steps.map((step, index) => (
                <div key={step.title} className={styles.stepItem}>
                  <Body1Strong>{i18n.getString(step.title)}</Body1Strong>
                  {step.description && (
                    <Body1 className={styles.stepDescription}>
                      {i18n.getString(step.description)}
                    </Body1>
                  )}
                  {step.expandLabel && (
                    <>
                      <div
                        className={mergeClasses('troubleshoot-step-expand', styles.expandLink)}
                        role="button"
                        tabIndex={0}
                        aria-expanded={expandedSteps.has(index)}
                        onClick={() => toggleStep(index, step.title)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleStep(index, step.title); } }}
                      >
                        <ChevronRight12Regular
                          className={mergeClasses(
                            styles.chevronIcon,
                            expandedSteps.has(index) && styles.chevronExpanded
                          )}
                        />
                        <Body1Strong>{i18n.getString(step.expandLabel)}</Body1Strong>
                      </div>
                      {expandedSteps.has(index) && step.expandSteps && (
                        <ol className={mergeClasses('troubleshoot-step-expand-content', styles.expandedStepList)}>
                          {step.expandSteps.map(text => (
                            <li key={text}>
                              <Caption1>
                                <ReactTemplate
                                  template={i18n.getString(text)}
                                  model={boldModel}
                                />
                              </Caption1>
                            </li>
                          ))}
                        </ol>
                      )}
                      {expandedSteps.has(index) && step.expandNote && (
                        <Caption1 className={styles.expandNote}>
                          {i18n.getString(step.expandNote)}
                        </Caption1>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {content.secondarySection && (
            <div className={mergeClasses('troubleshoot-detail-secondary-section', styles.stepsSection)}>
              <Body1Strong>{i18n.getString(content.secondarySection.title)}</Body1Strong>
              <div className={styles.stepsList}>
                {content.secondarySection.items.map((item, index) => (
                  <div key={item.title} className={mergeClasses('troubleshoot-accordion-item', styles.accordionItem)}>
                    <div
                      className={mergeClasses('troubleshoot-accordion-header', styles.accordionHeader)}
                      role="button"
                      tabIndex={0}
                      aria-expanded={expandedAccordions.has(index)}
                      onClick={() => toggleAccordion(index, item.title)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleAccordion(index, item.title); } }}
                    >
                      <ChevronRight12Regular
                        className={mergeClasses(
                          styles.chevronIcon,
                          expandedAccordions.has(index) && styles.chevronExpanded
                        )}
                      />
                      <Body1Strong>{i18n.getString(item.title)}</Body1Strong>
                    </div>
                    {expandedAccordions.has(index) && (
                      <div className={styles.accordionContent}>
                        <Body1>
                          <ReactTemplate
                            template={i18n.getString(item.content)}
                            model={{
                              a: (text: string) => (
                                <Link
                                  href={item.contentLinkUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  inline
                                  onClick={() => logger?.trace(`TroubleshootDetailDrawer: Clicked content link url=${item.contentLinkUrl}`)}
                                >
                                  {text}
                                </Link>
                              ),
                            }}
                          />
                          {item.seeExampleLabel && (
                            <>
                              {' '}
                              <span className={styles.seeExampleLabel}>
                                {i18n.getString(item.seeExampleLabel)}
                              </span>
                            </>
                          )}
                        </Body1>
                        {item.seeExampleContent && (
                          <Caption1 className={styles.seeExampleContent}>
                            {i18n.getString(item.seeExampleContent)}
                          </Caption1>
                        )}
                        {item.note && (
                          <Caption1 className={styles.noteText}>
                            <ReactTemplate
                              template={i18n.getString(item.note)}
                              model={{
                                a: (text: string) => (
                                  <Link
                                    href={item.noteLinkUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    inline
                                    onClick={() => logger?.trace(`TroubleshootDetailDrawer: Clicked note link url=${item.noteLinkUrl}`)}
                                  >
                                    {text}
                                  </Link>
                                ),
                              }}
                            />
                          </Caption1>
                        )}
                        {item.googleNote && (
                          <Caption1 className={styles.googleNote}>
                            <ReactTemplate
                              template={i18n.getString(item.googleNote)}
                              model={boldItalicModel}
                            />
                          </Caption1>
                        )}
                        {item.whatToDoLabel && (
                          <>
                            <div
                              className={styles.expandLink}
                              role="button"
                              tabIndex={0}
                              aria-expanded={expandedWhatToDo.has(index)}
                              aria-controls={`whatToDoContent-${index}`}
                              onClick={() => toggleWhatToDo(index)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleWhatToDo(index); } }}
                            >
                              <ChevronRight12Regular
                                className={mergeClasses(
                                  styles.chevronIcon,
                                  expandedWhatToDo.has(index) && styles.chevronExpanded
                                )}
                              />
                              <Body1Strong>{i18n.getString(item.whatToDoLabel)}</Body1Strong>
                            </div>
                            {expandedWhatToDo.has(index) && item.whatToDoItems && (
                              <div id={`whatToDoContent-${index}`} className={styles.whatToDoContent}>
                                {item.whatToDoItems.map(whatToDo => (
                                  <Body1 key={whatToDo.text}>
                                    {whatToDo.linkUrl ? (
                                      <ReactTemplate
                                        template={i18n.getString(whatToDo.text)}
                                        model={{
                                          a: (text: string) => (
                                            <Link
                                              href={whatToDo.linkUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              inline
                                              onClick={() => logger?.trace(`TroubleshootDetailDrawer: Clicked whatToDo link url=${whatToDo.linkUrl}`)}
                                            >
                                              {text}
                                            </Link>
                                          ),
                                        }}
                                      />
                                    ) : (
                                      <ReactTemplate
                                        template={i18n.getString(whatToDo.text)}
                                        model={boldItalicModel}
                                      />
                                    )}
                                  </Body1>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={mergeClasses('troubleshoot-detail-support-section', styles.supportSection)}>
          <Body1>
            <ReactTemplate
              template={i18n.getString(_TL_('Still have questions? {{a}}Contact support{{/a}}'))}
              model={{
                a: (text: string) => (
                  <Link
                    href={CONTACT_SUPPORT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    inline
                    onClick={() => logger?.trace('TroubleshootDetailDrawer: Clicked Contact support link')}
                  >
                    {text}
                  </Link>
                ),
              }}
            />
          </Body1>
          <Caption1 className={styles.supportCaption}>
            {i18n.getString(_TL_('For account security, we may ask you to verify your identity'))}
          </Caption1>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <Button appearance="primary" onClick={handleClose}>
          {i18n.getString(_TL_('Close'))}
        </Button>
      </DrawerFooter>
    </StackedDrawer>
  );
};
